<template>
  <form class="station-form" @submit.prevent="submit" autocomplete="off">
    <div class="form-title">{{ isEdit ? '编辑充电站' : '添加充电站' }}</div>

    <label class="field">
      <span>充电站名称 *</span>
      <input v-model.trim="form.name" placeholder="如：市民中心充电站" autocomplete="off" required />
    </label>

    <label class="field">
      <span>详细地址 *</span>
      <input v-model.trim="form.address" placeholder="如：xx区xx路xx号停车场" autocomplete="off" required />
    </label>

    <div class="field-row">
      <label class="field">
        <span>充电桩总数 *</span>
        <input v-model.number="form.totalStalls" type="number" inputmode="numeric" min="1" required />
      </label>

      <label class="field">
        <span>最大功率（kW）</span>
        <input v-model.number="form.maxPower" type="number" inputmode="numeric" min="0" placeholder="如：120" />
      </label>
    </div>

    <div class="tariff-title">
      分时段价格
      <button type="button" class="btn-mini" @click="addTariff">+ 添加时段</button>
    </div>

    <div class="templates">
      <span class="templates-label">快速填入：</span>
      <button type="button" class="tpl-btn" v-for="tpl in templates" :key="tpl.name" @click="applyTemplate(tpl)">{{ tpl.name }}</button>
    </div>

    <div class="tariff-tip">首尾固定（00:00、24:00），中间时间点可点击直接选择，修改后相邻时段自动衔接；新增时段自动沿用前一段的价格</div>

    <div v-for="(t, i) in form.tariffs" :key="i" class="tariff-row">
      <span v-if="i === 0" class="tariff-time-text locked">00:00</span>
      <input
        v-else
        type="time"
        class="tariff-time"
        step="3600"
        :value="t.start"
        :min="startMin(i)"
        :max="startMax(i)"
        @change="onStart(i, $event.target.value)"
      />
      <span class="tariff-sep">-</span>
      <span v-if="i === form.tariffs.length - 1" class="tariff-time-text locked">24:00</span>
      <input
        v-else
        type="time"
        class="tariff-time"
        step="3600"
        :value="t.end"
        :min="endMin(i)"
        :max="endMax(i)"
        @change="onEnd(i, $event.target.value)"
      />
      <select v-model="t.label" class="tariff-label">
        <option>谷时</option>
        <option>平时</option>
        <option>峰时</option>
        <option>统一</option>
      </select>
      <input v-model.number="t.price" type="number" inputmode="decimal" step="0.01" min="0" class="tariff-price" placeholder="元/度" />
      <button
        v-if="form.tariffs.length > 1"
        type="button"
        class="btn-x"
        @click="removeRow(i)"
      >×</button>
    </div>

    <div class="form-actions">
      <button type="button" class="btn ghost" @click="$emit('cancel')">取消</button>
      <button type="submit" class="btn primary">保存充电站</button>
    </div>
  </form>
</template>

<script>
function toMin(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
function toHHMM(min) {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
}

function parsePeriod(period) {
  // "00:00 - 08:00" -> { start: '00:00', end: '08:00' }
  const m = (period || '').match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/)
  return m ? { start: m[1].padStart(5, '0'), end: m[2].padStart(5, '0') } : { start: '00:00', end: '08:00' }
}

// 新增站点的默认时段：谷/平/峰 三段
const DEFAULT_TARIFFS = [
  { start: '00:00', end: '08:00', label: '谷时', price: 0.45 },
  { start: '08:00', end: '18:00', label: '平时', price: 0.95 },
  { start: '18:00', end: '24:00', label: '峰时', price: 1.45 }
]

const TEMPLATES = [
  {
    name: '深圳分时',
    tariffs: [
      { start: '00:00', end: '08:00', label: '谷时', price: 0.45 },
      { start: '08:00', end: '10:00', label: '平时', price: 0.95 },
      { start: '10:00', end: '15:00', label: '峰时', price: 1.45 },
      { start: '15:00', end: '18:00', label: '平时', price: 0.95 },
      { start: '18:00', end: '21:00', label: '峰时', price: 1.45 },
      { start: '21:00', end: '24:00', label: '平时', price: 0.95 }
    ]
  },
  {
    name: '统一电价',
    tariffs: [
      { start: '00:00', end: '24:00', label: '统一', price: 1.00 }
    ]
  },
  {
    name: '峰谷平',
    tariffs: [
      { start: '00:00', end: '08:00', label: '谷时', price: 0.40 },
      { start: '08:00', end: '18:00', label: '平时', price: 0.80 },
      { start: '18:00', end: '24:00', label: '峰时', price: 1.30 }
    ]
  }
]

// 把含 start/end 的时段数组转为行数据：按开始时间排序，首尾固定为 00:00 / 24:00
function toRows(tariffs) {
  const rows = tariffs
    .map(t => {
      const { start, end } = t.start ? { start: t.start, end: t.end } : parsePeriod(t.period)
      return { start, end, label: t.label, price: Number(t.price) }
    })
    .sort((a, b) => toMin(a.start) - toMin(b.start))
    .map(t => ({ ...t }))
  if (rows.length) {
    rows[0].start = '00:00'
    rows[rows.length - 1].end = '24:00'
  }
  return rows
}

export default {
  name: 'StationForm',
  props: {
    initial: { type: Object, default: null }
  },
  emits: ['save', 'cancel'],
  data() {
    const f = this.initial
    return {
      templates: TEMPLATES,
      form: {
        name: f ? f.name : '',
        address: f ? f.address : '',
        totalStalls: f ? f.totalStalls : 10,
        maxPower: f ? (f.maxPower ?? '') : '',
        tariffs: f && f.tariffs && f.tariffs.length
          ? toRows(f.tariffs)
          : toRows(DEFAULT_TARIFFS)
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.initial
    }
  },
  methods: {
    // 开始时间选择范围：晚于上一段开始、早于本段结束（输入框 min/max）
    startMin(i) {
      return i > 0 ? this.form.tariffs[i - 1].start : '00:00'
    },
    startMax(i) {
      return this.form.tariffs[i].end
    },
    // 结束时间选择范围：晚于本段开始、早于下一段结束（24:00 不是合法 time 值，用 23:59）
    endMin(i) {
      return this.form.tariffs[i].start
    },
    endMax(i) {
      const next = this.form.tariffs[i + 1]
      if (!next || next.end === '24:00') return '23:59'
      return next.end
    },
    // 修改某行开始时间 = 同步上一行结束时间（边界点共享）
    onStart(i, v) {
      const rows = this.form.tariffs
      const lo = i > 0 ? toMin(rows[i - 1].start) : 0
      const hi = toMin(rows[i].end)
      if (!v || toMin(v) % 60 !== 0 || toMin(v) <= lo || toMin(v) >= hi) {
        window.alert('时间必须为整点，且在上一段开始之后、本段结束之前')
        this.$forceUpdate()
        return
      }
      rows[i].start = v
      if (i > 0) rows[i - 1].end = v
    },
    // 修改某行结束时间 = 同步下一行开始时间（边界点共享）
    onEnd(i, v) {
      const rows = this.form.tariffs
      const lo = toMin(rows[i].start)
      const hi = i < rows.length - 1 ? toMin(rows[i + 1].end) : 24 * 60
      if (!v || toMin(v) % 60 !== 0 || toMin(v) <= lo || toMin(v) >= hi) {
        window.alert('时间必须为整点，且在本段开始之后、下一段结束之前')
        this.$forceUpdate()
        return
      }
      rows[i].end = v
      if (i < rows.length - 1) rows[i + 1].start = v
    },
    // 第 i 行的中点（按整点取整）
    midpoint(i) {
      const s = toMin(this.form.tariffs[i].start)
      const e = toMin(this.form.tariffs[i].end)
      return Math.round((s + e) / 2 / 60) * 60
    },
    // 添加时段：把最后一段一分为二，新时段自动沿用前一段的类型和价格
    addTariff() {
      const rows = this.form.tariffs
      const last = rows.length - 1
      const mid = this.midpoint(last)
      const s = toMin(rows[last].start)
      if (mid <= s || mid >= toMin(rows[last].end)) {
        window.alert('最后一个时段太短，无法继续添加')
        return
      }
      const midStr = toHHMM(mid)
      rows[last].end = midStr
      rows.push({ start: midStr, end: '24:00', label: rows[last].label, price: rows[last].price })
    },
    // 删除行：首尾保持固定，剩余时段自动衔接
    removeRow(i) {
      const rows = this.form.tariffs
      if (i === 0) {
        rows.splice(0, 1)
        rows[0].start = '00:00'
      } else if (i === rows.length - 1) {
        rows.splice(i, 1)
        rows[rows.length - 1].end = '24:00'
      } else {
        // 被删行的开始点（= 上一行结束）并到下一行开始，保持衔接
        rows[i - 1].end = rows[i + 1].start
        rows.splice(i, 1)
      }
    },
    applyTemplate(tpl) {
      this.form.tariffs = toRows(tpl.tariffs)
    },
    submit() {
      const f = this.form
      if (!f.name || !f.address) return
      const tariffs = f.tariffs
        .map(t => ({
          label: t.label,
          period: `${t.start} - ${t.end}`,
          price: Number(t.price)
        }))
        .filter(t => Number.isFinite(t.price))
      this.$emit('save', { ...f, maxPower: f.maxPower || null, tariffs })
    }
  }
}
</script>

<style scoped>
.station-form {
  padding: 14px;
}
.form-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}
.field {
  display: block;
  margin-bottom: 10px;
}
.field-row {
  display: flex;
  gap: 10px;
}
.field-row .field {
  flex: 1;
  min-width: 0;
}
.field span {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}
.field input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
}
.field input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}
.tariff-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin: 6px 0 8px;
}
.tariff-tip {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
  line-height: 1.5;
}
.tariff-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  align-items: center;
}
.tariff-time-text {
  flex: 0.75;
  padding: 6px 4px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  white-space: nowrap;
}
.tariff-time-text.locked {
  color: #94a3b8;
  background: #f1f5f9;
}
.tariff-time {
  flex: 0.9;
  padding: 6px 4px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
}
.tariff-sep {
  color: #94a3b8;
  font-size: 12px;
  flex-shrink: 0;
}
.templates {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.templates-label {
  font-size: 12px;
  color: #64748b;
  flex-shrink: 0;
}
.tpl-btn {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  color: #475569;
  white-space: nowrap;
}
.tpl-btn:hover {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
}
.tariff-period {
  flex: 1.6;
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
}
.tariff-label {
  flex: 0.8;
  padding: 6px 4px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
}
.tariff-price {
  flex: 0.8;
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
}
.btn-x {
  border: none;
  background: none;
  font-size: 18px;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 4px;
}
.btn-x:hover {
  color: #dc2626;
}
.btn-mini {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
}
.btn-mini:hover {
  border-color: #2563eb;
  color: #2563eb;
}
.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}
.btn {
  flex: 1;
  padding: 9px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}
.btn.primary {
  background: #2563eb;
  color: #fff;
}
.btn.primary:hover {
  background: #1d4ed8;
}
.btn.ghost {
  background: #fff;
  border-color: #cbd5e1;
  color: #475569;
}

/* 手机（<=640px） */
@media (max-width: 640px) {
  .station-form {
    padding: 12px 12px 0;
  }
  .field input {
    font-size: 16px;
    padding: 10px 12px;
  }
  /* 时段行卡片化：第一行时间范围，第二行类型+价格 */
  .tariff-row {
    flex-wrap: wrap;
    gap: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px;
    margin-bottom: 10px;
  }
  .tariff-time,
  .tariff-time-text {
    flex: 1 1 0;
    min-width: 0;
    font-size: 16px;
    padding: 9px 4px;
    text-align: center;
  }
  .tariff-sep {
    flex: 0 0 auto;
  }
  .tariff-label {
    flex: 0 0 32%;
    order: 4;
  }
  .tariff-price {
    flex: 1 1 0;
    order: 5;
  }
  .btn-x {
    order: 6;
    flex: 0 0 auto;
    align-self: center;
    font-size: 22px;
    padding: 6px 10px;
  }
  .tariff-row input,
  .tariff-row select {
    font-size: 16px;
    padding-top: 9px;
    padding-bottom: 9px;
  }
  .tpl-btn {
    padding: 7px 14px;
    font-size: 12px;
  }
  .btn-mini {
    padding: 8px 14px;
    font-size: 13px;
  }
  /* 底部固定操作栏，方便单手提交 */
  .form-actions {
    position: sticky;
    bottom: 0;
    z-index: 5;
    margin: 14px -12px 0;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.08);
  }
  .btn {
    padding: 13px;
    font-size: 15px;
  }
}
</style>
